import { createColumnHelper, type Row } from "@tanstack/react-table";
import type { MapGeoJSONFeature } from "maplibre-gl";

import { Button, type AttributesLayoutHelper } from "safesystem_client-component-lib";

import { EAccidents, EIdentify, EPrioritize } from "./interfaces";
import { SafeRoadsCalculatorLayoutHelper } from "@/components/SafeRoadsCalculator/SafeRoadsCalculator";
import { updateViewParam } from "@/lib/util";
import { useFeatureSelection } from "@/lib/featureSelectionContext";

export type TIdentifyRoads = {
	state: string;
	preferred_street_name: string;
	safe_roads_rating_band: number;
	converted_speed_limit_mph: number;
	aadt: number;
	lane_count: number;
	land_use: string;
	road_stereotype: string;
	shoulder_width_category: string;
	lane_width_category: string;
	horizontal_alignment: string;
	roadside_hazard: string;
	intersection_density: string;
	access_density: string;
	traffic_volume: number;
	dataversion: number;
	_feature: MapGeoJSONFeature;
};

export type TPrioritizeCorridors = {
	vkt_percentage_cumulative_int: string;
	vkt_prioritize: string;
	_feature: MapGeoJSONFeature;
};

export const infoTextIdentify = `Segments are categorized according to star rating. 1 star segments have the lowest rating and 5 star the highest.`;
export const infoTextAccidents = `The information shown is from the Fatality Analysis Reporting System (FARS) which is a national dataset of all fatal motor vehicle crashes prepared by the National Highway Traffic Safety Administration (NHTSA).`;
export const infoTextPrioritize = `The network is prioritized by VMT. The portions of the network with the top 10% of travel are categorized Priority 1.`;

const showOnMap = (row: Row<TIdentifyRoads> | Row<TPrioritizeCorridors>) => {
	const { setSelectedFeatures } = useFeatureSelection();
	const mapShowFeature = () => {
		const { _feature } = row.original;
		_feature.properties._source = "table";
		setSelectedFeatures([_feature]);
		updateViewParam("map");
	};

	return (
		<Button onClick={() => mapShowFeature()} variant="text">
			Show on map
		</Button>
	);
};

export type StarRatingContextKey = "1 star" | "2 star" | "3 star" | "4 star" | "5 star";

const colHelperIdentifyRoads = createColumnHelper<TIdentifyRoads>();

export const colsIdentifyRoads = [
	colHelperIdentifyRoads.accessor("preferred_street_name", {
		header: () => "Road name",
		cell: (props) => <strong>{props.getValue()}</strong>,
	}),
	colHelperIdentifyRoads.accessor("safe_roads_rating_band", {
		header: () => "Star rating",
	}),
	colHelperIdentifyRoads.accessor("converted_speed_limit_mph", {
		header: () => "Speed limit",
	}),
	colHelperIdentifyRoads.accessor("land_use", {
		header: () => "Land use",
	}),
	colHelperIdentifyRoads.accessor("road_stereotype", {
		header: () => "Road stereotype",
	}),
	colHelperIdentifyRoads.accessor("shoulder_width_category", {
		header: () => "Shoulder width",
	}),
	colHelperIdentifyRoads.accessor("lane_width_category", {
		header: () => "Lane width",
	}),
	colHelperIdentifyRoads.accessor("horizontal_alignment", {
		header: () => "Horizontal alignment",
	}),
	colHelperIdentifyRoads.accessor("roadside_hazard", {
		header: () => "Roadside hazard",
	}),
	colHelperIdentifyRoads.accessor("intersection_density", {
		header: () => "Intersection density",
	}),
	colHelperIdentifyRoads.accessor("access_density", {
		header: () => "Access density",
	}),
	colHelperIdentifyRoads.accessor("traffic_volume", {
		header: () => "Traffic volume",
	}),
	colHelperIdentifyRoads.display({
		id: "actions",
		cell: (props) => showOnMap(props.row),
	}),
];

const attributesIdentifyRoads: SafeRoadsCalculatorLayoutHelper = {
	title: { accessorKey: "safe_roads_rating_band" },
	subheaders: [
		[
			{
				accessorKey: "preferred_street_name",
			},
			{
				accessorKey: "county"
			}
		],
	],
	infoText: "Segments are categorized according to star rating. 1 star segments have the lowest rating and 5 star the highest.",
};

export const priority_map: Record<string, string> = {
	"Priority 1": "Priority 1 (10%)",
	"Priority 2": "Priority 2 (15%)",
	"Priority 3": "Priority 3 (20%)",
	"Priority 4": "Priority 4 (30%)",
	"Priority 5": "Priority 5 (25%)",
};

const colHelperPrioritizeCorridors = createColumnHelper<TPrioritizeCorridors>();

export const colsPrioritizeCorridors = [
	colHelperPrioritizeCorridors.accessor("vkt_percentage_cumulative_int", {
		header: () => "Cumulative VKT",
		cell: (props) => {
			return <strong>{props.getValue()}</strong>;
		},
	}),
	colHelperPrioritizeCorridors.accessor("vkt_prioritize", {
		header: () => "VKT band",
		cell: (info) => priority_map[info.getValue()],
		enableSorting: true,
	}),
	colHelperPrioritizeCorridors.display({
		id: "actions",
		cell: (props) => showOnMap(props.row),
	}),
];

const attributesPrioritizeCorridors: AttributesLayoutHelper = {
	title: { accessorKey: "vkt_prioritize", cell: (value) => `Corridor ${value}` },
	subheaders: [
		[
			{
				accessorKey: "vkt_prioritize",
			}
		]
	],
	body: [
		{
			accessorKey: "vkt_percentage_cumulative_int",
			header: () => "Cumulative VKT",
			show: (info) => info !== undefined,
			cell: (info) => `${info}%`,
		},
	],
	table: [
	],
	infoText: "The network is prioritized by VMT. The portions of the network with the top 10% of travel are categorized Priority 1.",
};

const attributesAccidents: AttributesLayoutHelper = {
	title: { accessorKey: "id", cell: () => "Fatal crash" },
	subheaders: [
		[
			{
				accessorKey: "road_name",
			},
			{
				accessorKey: "county",
			},
		],
	],
	body: [
		{
			accessorKey: "year",
			header: () => "Crash year",
		},
		{
			accessorKey: "fatal_count",
			header: () => "Number of fatalities",
		},
	],
	table: [
		{
			accessorKey: "road_func_class",
			header: () => "Roadway functional classification",
		},
		{
			accessorKey: "veh_involved_count",
			header: () => "Number of vehicles involved",
		},
		{
			accessorKey: "date",
			header: () => "Date",
			cell: (info) => new Date(info).toLocaleDateString(),
		},
	],
	addenda: [],
	infoText:
		"The information shown is from the Fatality Analysis Reporting System (FARS) which is a national dataset of all fatal motor vehicle crashes prepared by the National Highway Traffic Safety Administration (NHTSA).",
};


const attributesUploadedCrashes: AttributesLayoutHelper = {
	title: { accessorKey: "id", cell: () => "Uploaded crash data" },
	subheaders: [
		[
			{
				header: () => "Severity",
				accessorKey: "Severity",
			},
		],
	],
	body: [
		{
			header: () => "Fatalities",
			accessorKey: "Fatalities",
		},
	],
	table: [
		{
			header: () => "Date of Incident",
			accessorKey: "Date of Incident",
			cell: (info) => new Date(info).toLocaleDateString(),
		},
		{
			header: () => "Manner of Collision",
			accessorKey: "Manner of Collision",
		},
		{
			header: () => "Location Type",
			accessorKey: "Location Type",
		},
		{
			header: () => "Surface Condition",
			accessorKey: "Surface Condition",
		},
	],
	infoText: infoTextAccidents,
};

export const attributesColumns = {
	[EIdentify.Identify]: attributesIdentifyRoads,
	[EPrioritize.Prioritize]: attributesPrioritizeCorridors,
	// [EInterventions.CurveAdvisorySigns]: attributesInterventionsSignage,
	// [EInterventions.CurveAdvisorySpeeds]: attributesInterventionsSpeeds,
	[EAccidents.AccidentsCircle]: attributesAccidents,
	[EAccidents.UploadedCrashes]: attributesUploadedCrashes,
};

export const layerTypeToQueryPropertiesMap = {
	[EIdentify.Identify]: ["road_name", "county"],
	[EPrioritize.Prioritize]: ["county"],
	[EAccidents.AccidentsHeatmap]: ["county"],
	[EAccidents.AccidentsCircle]: ["county"],
	[EAccidents.UploadedCrashes]: []
};
